/** @file keyEntries: the rows of a key-row cell's menu (key, delete, copy / paste pose, ease, event). */
import { keyAt } from '@zilo/pose3d-commands/clip/keyAt';
import type { Ease } from '@zilo/pose3d-format/types/clip';
import { currentClip } from '../../panels/timeline/currentClip';
import { deleteKeysCommand } from '../../panels/timeline/deleteKeysCommand';
import { keyFrame } from '../../panels/timeline/keyFrame';
import { mirrorGuard } from '../../panels/timeline/mirrorGuard';
import { pastePose } from '../../panels/timeline/pastePose';
import type { EditorStore } from '../../state/editorTypes';
import type { KeyMenuCtx, MenuEntry } from './registryTypes';

type Entry = MenuEntry<KeyMenuCtx>;
const EASES: Ease[] = ['linear', 'easeIn', 'easeOut', 'easeInOut', 'step'];
const hasClip = (c: KeyMenuCtx): boolean => currentClip(c.store.getState()) !== null;
const deletion = (c: KeyMenuCtx) => {
  const cur = currentClip(c.store.getState());
  return cur === null || c.node === null ? null : deleteKeysCommand(cur.name, cur.clip, [{ node: c.node, frame: c.frame }]);
};
const keyOf = (c: KeyMenuCtx) => {
  const cur = currentClip(c.store.getState());
  return cur === null ? undefined : keyAt(cur.clip, c.frame);
};

/** Appends a `step` event on `node` (if any) to the frame's events. */
function addEvent(store: EditorStore, frame: number, node: string | null): void {
  const s = store.getState();
  const cur = currentClip(s);
  if (cur === null || mirrorGuard(cur.clip, frame, s.notify)) return;
  const events = [...(keyAt(cur.clip, frame)?.events ?? []), node === null ? { type: 'step' as const } : { type: 'step' as const, node }];
  void s.dispatch([{ name: 'clip.setEvents', input: { clip: cur.name, frame, events } }], `add event @${frame}`);
}

const easeEntry = (ease: Ease): Entry => ({
  kind: 'item', id: `key.ease.${ease}`, when: hasClip, label: (c) => `Ease: ${ease}${keyOf(c)?.ease === ease ? ' (set)' : ''}`,
  disabled: (c) => keyOf(c) === undefined,
  run: (c) => {
    const cur = currentClip(c.store.getState());
    if (cur === null) return;
    const keys = [{ frame: c.frame, nodes: {}, ease }];
    void c.store.getState().dispatch([{ name: 'clip.setKeys', input: { clip: cur.name, keys, merge: 'mergeNodes' } }], `ease ${ease} @${c.frame}`);
  },
});

/** Every row needs a clip on the playhead (without one the menu is empty). */
export const KEY_ENTRIES: Entry[] = [
  {
    kind: 'item', id: 'key.here', when: hasClip, chords: (c) => [c.node === null ? 'transport.keyAll' : 'transport.keyJoint'],
    label: (c) => (c.node === null ? 'Key whole pose here' : `Key ${c.node} here`),
    run: (c) => { keyFrame(c.store, c.frame, c.node === null ? undefined : [c.node]); },
  },
  {
    kind: 'item', id: 'key.delete', label: () => 'Delete key', when: hasClip, chords: ['timeline.deleteKeys'], disabled: (c) => deletion(c) === null,
    run: (c) => {
      const del = deletion(c);
      if (del !== null) void c.store.getState().dispatch([del], 'delete key');
    },
  },
  { kind: 'separator' },
  {
    kind: 'item', id: 'key.copy', label: () => 'Copy pose from here', when: hasClip, chords: ['timeline.copyPose'],
    run: (c) => {
      const cur = currentClip(c.store.getState());
      if (cur !== null) c.clipboard.current = { clip: cur.name, frame: c.frame };
    },
  },
  {
    kind: 'item', id: 'key.paste', label: () => 'Paste pose here', when: hasClip, chords: ['timeline.pastePose'], disabled: (c) => c.clipboard.current === null,
    run: (c) => { pastePose(c.store, c.clipboard, c.frame); },
  },
  { kind: 'separator' },
  ...EASES.map(easeEntry),
  { kind: 'separator' },
  { kind: 'item', id: 'key.event', label: () => 'Add event...', when: hasClip, run: (c) => { addEvent(c.store, c.frame, c.node); } },
];
